'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { Icons } from '@/components/icons';
import { createCalendar } from '@internationalized/date';
import { useRef } from 'react';
import type { AriaDatePickerProps, DateValue } from 'react-aria';
import { useDateField, useLocale } from 'react-aria';
import { useDateFieldState } from 'react-stately';
import { DateSegment } from './date-segment';

export type AdditionalDateFieldProps = {
  hideResetButton?: boolean;
  className?: string;
};

function DateField(
  props: AriaDatePickerProps<DateValue> & AdditionalDateFieldProps
) {
  const ref = useRef<HTMLDivElement | null>(null);

  const { locale } = useLocale();
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });
  const { fieldProps } = useDateField(props, state, ref);
  return (
    <div
      className={cn(
        'flex h-11 w-full items-center justify-between rounded-l-input border border-r-0 border-input group-focus-within/datepicker:border-ring group-data-[invalid=true]/datepicker:border-destructive group-data-[invalid=true]/field:border-destructive',
        props.className
      )}
    >
      <div
        {...fieldProps}
        ref={ref}
        className={cn(
          'inline-flex w-full flex-1 items-center bg-transparent px-4 py-2 text-sm focus-visible:outline-hidden',
          props.isDisabled ? 'cursor-not-allowed opacity-50' : ''
        )}
      >
        {state.segments.map((segment, i) => {
          if (segment.type === 'timeZoneName') {
            return null;
          }
          return <DateSegment key={i} segment={segment} state={state} />;
        })}
        {state.isInvalid && (
          <Icons.blocked className='ml-1 size-4 text-destructive' />
        )}
      </div>
      {state.value && !props.hideResetButton && !props.isDisabled && (
        <Button
          onClick={() => state.setValue(null)}
          type='button'
          variant={'ghost'}
          size={'icon'}
          className='mr-2 text-muted-foreground transition-colors hover:text-foreground'
        >
          <Icons.close className='size-4' />
          <span className='sr-only'>Rensa</span>
        </Button>
      )}
    </div>
  );
}

export { DateField };
